import { getBackendErrors, getErrorMessage } from "api/errors";
import { useExports } from "api/exports";
import { hasViewAccessToResource } from "api/general";
import { useSegments } from "api/segments";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import {
  SEGMENTS_ASSOCIATED_ACHIEVEMENTS_LIST_ID,
  SEGMENTS_ASSOCIATED_CAMPAIGNS_LIST_ID,
  SEGMENTS_ASSOCIATED_REWARDS_LIST_ID,
  SEGMENTS_LIST_ID,
} from "state/tables/types";
import { ExportType } from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { SegmentData } from "types/segments";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatCurrencyValue, formatNumberValue } from "utilities/numbers";
import { DeactivateSegmentModal } from "./DeactivateSegmentModal";

function createItems(items: SegmentData[]): Row[] {
  return items.map((item) => ({
    cells: [
      { content: item.name, property: "name" },
      {
        align: "right",
        content: formatNumberValue(item.customersCount),
        emptyString: "0",
        property: "customersCount",
      },
      {
        align: "right",
        content: formatCurrencyValue(item.averageTransactionAmount),
        property: "averageTransactionAmount",
      },
      {
        content: OLDate(item.calculatedAt),
        property: "calculatedAt",
      },
      {
        align: "right",
        content: formatNumberValue(item.averageTransactions),
        emptyString: "0",
        property: "averageTransactions",
      },
      {
        align: "right",
        content: formatCurrencyValue(item.averageClv),
        property: "averageClv",
      },
      { content: OLDate(item.createdAt), property: "createdAt" },
    ],
    id: item.segmentId,
    metadata: { disabled: !item.active },
    testId: "segments-list-row",
  }));
}

enum Modals {
  Activate,
  Deactivate,
  Remove,
  Export,
}

export function SegmentList() {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { listing, updateListing } = useListing(SEGMENTS_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[SEGMENTS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      SEGMENTS_LIST_ID,
    );

    setFilters(filters);
  };

  const { currency } = useAppSelector(selectCurrentStoreData);

  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    segment: string;
    mode: number;
  }>();

  const { activateSegment, removeSegment, useSegmentsList } = useSegments();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();

  const { data, error } = useSegmentsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const hasViewExportPermission = hasViewAccessToResource("EXPORT");

  const getSegmentName = (id: string) =>
    data?.items.find(({ segmentId }) => segmentId === id)?.name;

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      hasMinWidthThreshold: true,
      testId: "segments-table-header-name",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "members" }),
      property: "customersCount",
      isSortable: true,
    },
    {
      headerText: message({ id: "lastRecalculation" }),
      property: "calculatedAt",
      isSortable: true,
      tooltip: (
        <ExternalLinkTooltipContent
          name="lastRecalculation"
          text={message({ id: "lastRecalculationInfo" })}
        />
      ),
    },
    {
      align: "right",
      headerText: message(
        { id: "avgTransactionValueWithCurrency" },
        { currency },
      ),
      property: "averageTransactionAmount",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "avgNumberOfTransactions" }),
      property: "averageTransactions",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "avgSpendingWithCurrency" }, { currency }),
      property: "averageClv",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
  ]);

  const items = data?.items ? createItems(data.items) : [];

  const { exportList } = useExports();
  const handleExportMemberList = async () => {
    const data = await exportList(ExportType.MEMBER_SEGMENT, {
      memberSegment: {
        _orderBy: { createdAt: "desc" },
        _page: listing.page,
        _itemsOnPage: listing.rowsPerPage,
        segmentId: selectedModal?.segment,
      },
    });

    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();

            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setOpen(false);
    }
  };

  const modalSegmentName = data?.items.find(
    (segment) => segment.segmentId === selectedModal?.segment,
  )?.name;

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, SEGMENTS_LIST_ID);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  const [errors, setErrors] = useState<any>();
  const handleCloseModal = () => {
    setOpen(false);
    setErrors(null);
  };

  return (
    <>
      <TopHeader title={message({ id: "segments" })} />
      <Subheader>
        <FiltersV2
          data-test-id="segments"
          donutLabel={message({ id: "segments" })}
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "averageClv",
              label: message({ id: "avgSpendingWithCurrency" }, { currency }),
              type: "number",
            },
            {
              query: "averageTransactions",
              label: message({ id: "avgNumberOfTransactions" }),
              type: "number",
            },
            {
              query: "averageTransactionAmount",
              label: message(
                { id: "avgTransactionValueWithCurrency" },
                { currency },
              ),
              type: "number",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "description",
              label: message({ id: "description" }),
              type: "text",
            },
            {
              query: "customersCount",
              label: message({ id: "members" }),
              type: "number",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "segment-single",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <SubheaderLinkButton
          text={message({ id: "addSegment" })}
          link={links.addSegment}
          data-test-id="segments-add-segment-button"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: SEGMENTS_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={SEGMENTS_LIST_ID}
        data-test-id="segments"
        onRowClick={(id: string) => {
          navigate(links.segment(id as string), {
            state: { segmentName: getSegmentName(id) },
          });
          appDispatch(
            clearTablePage({
              tableName: SEGMENTS_ASSOCIATED_CAMPAIGNS_LIST_ID,
            }),
          );
          appDispatch(
            clearTablePage({ tableName: SEGMENTS_ASSOCIATED_REWARDS_LIST_ID }),
          );
          appDispatch(
            clearTablePage({
              tableName: SEGMENTS_ASSOCIATED_ACHIEVEMENTS_LIST_ID,
            }),
          );
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ segment: id as string, mode: Modals.Activate });
                setOpen(true);
              },
              hideCondition: (item: Row) => !item.metadata?.disabled,
              label: message({
                id: "activateSegment",
              }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  segment: id as string,
                  mode: Modals.Deactivate,
                });
                setOpen(true);
              },
              hideCondition: (item: Row) => item.metadata?.disabled,
              label: message({
                id: "deactivateSegment",
              }),
            },
            {
              label: message({ id: "editSegment" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editSegment(id as string)),
            },
            {
              label: message({ id: "exportMembersList" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ segment: id as string, mode: Modals.Export });
                setOpen(true);
              },
              hideCondition: () => hasViewExportPermission,
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ segment: id as string, mode: Modals.Remove });
                setOpen(true);
              },
              label: message({ id: "removeSegment" }),
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) => {
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          });
        }}
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        clearFilters={() => setFilters([])}
      />
      {selectedModal?.mode === Modals.Activate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activateSegment(selectedModal?.segment);
              if (response) {
                setOpen(false);
                notify(message({ id: "segmentActivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => handleCloseModal()}
          cancelText={message({ id: "cancel" })}
          data-test-id="activate"
          errorInfo={errors?.[""]}
          header={
            <>
              {message(
                { id: "activateSegmentHeader" },
                { name: modalSegmentName },
              )}
            </>
          }
          open={open}
          submitText="yesActivate"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Deactivate ? (
        <DeactivateSegmentModal
          open={open}
          segmentId={selectedModal?.segment}
          segmentName={modalSegmentName}
          close={() => handleCloseModal()}
          prevPath={links.segmentsList}
        />
      ) : selectedModal?.mode === Modals.Remove ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await removeSegment(selectedModal?.segment);
              if (response) {
                setOpen(false);
                notify(message({ id: "segmentRemoved" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => handleCloseModal()}
          cancelText={message({ id: "cancel" })}
          data-test-id="remove-modal"
          errorInfo={errors?.[""]}
          header="theSegmentWillBeRemoved"
          info="deleteInfo"
          open={open}
          submitText="yes"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Export ? (
        <ConfirmationModal
          action={() => handleExportMemberList()}
          close={() => setOpen(false)}
          header="exportConfirmation"
          info=""
          open={open}
          cancelText={message({ id: "cancel" })}
          submitText="export"
          data-test-id="export"
          modalWidth={454}
        />
      ) : null}
    </>
  );
}
